/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT В©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';

import {BehaviorSubject, forkJoin, from, Observable, of, Subject} from 'rxjs';
import {catchError, filter, finalize, switchMap, takeUntil, tap} from 'rxjs/operators';

import cloneDeep from 'lodash-es/cloneDeep';

import {EmployeeVariablesService, SelectedVariable, VariableElementChanges} from './employee-variables.service';

import {DATE_FORMAT, DATE_MAX, DATE_MIN} from '../../../app.constants';
import {Destroyable} from '../../../destroyable.component';
import {EmpVariableAttributes, PayPeriodAttributes} from '../../../models/models.interfaces';
import {AnyData, Data} from '../../../shared/api';
import {VariableZonesComponent} from '../../../shared/employees/variable-zone/variable-zones.component';
import {Validators as GpValidators} from '../../../shared/forms';
import {ModalCommand, ModalService, ModalState} from '../../../shared/modal';
import {DatePickerInputComponent, TreeNode} from '../../../shared/ui';
import {groupBy} from '../../../shared/utils/arrays';
import {strToDate} from '../../../shared/utils/date';
import {isPresent} from '../../../shared/utils/lang';
import {at} from '../../../shared/utils/objects';
import {ToastrService} from 'ngx-toastr';

const moment = require('moment');
require('moment-range');

const getChildren = (types, type: string) => {
  return types[type].map(child => ({
    text: child.attributes.transId,
    value: child,
    data: child,
    selectable: true,
    visible: true,
    children: []
  }));
};

export enum VariableRecurrence {
  RECURRENT = 'RECURRENT',
  NON_RECURRENT = 'NON_RECURRENT',
  BOTH = 'BOTH'
}


@Component({
  selector: 'gp-payroll-prepare-variable-elements',
  templateUrl: 'variable-elements.component.html',
  styleUrls: ['./variable-elements.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VariableElementsComponent extends Destroyable implements OnChanges, OnInit {

  public dialog: any;

  @Input()
  public isActive: boolean = false;

  @Input()
  public isReadOnly: boolean = true;

  @Input()
  public cliRegId: number;

  @Input()
  public contract: any;

  public selectedVariable: SelectedVariable;
  public selectedItemType: any;
  public loading: boolean = false;
  public typeArrayData: any;
  public cancelEditItemModalCallback: () => Promise<void>;

  public submitEditItemModalCallback: () => Observable<ModalCommand>;

  public editedElement: Data<EmpVariableAttributes>;
  public variableElements: { [name: string]: Data<EmpVariableAttributes>[] };

  public payPeriods: Data<PayPeriodAttributes>[];
  public payPeriodId: number | undefined;

  public isLineDraw = false;
  public previousPayPeriod: Data<PayPeriodAttributes>;
  public editedPayPeriod: Data<PayPeriodAttributes>;

  public nextPayPeriod: Data<PayPeriodAttributes>;
  public evAmount: number;
  public _isDirty: boolean = false;
  public noPayPeriods: boolean;
  public isLongFuture: boolean = false;
  public infoForm: UntypedFormGroup;
  public isBeginBeforeEnd: boolean = true;
  public deletedVariableElements: Data<EmpVariableAttributes>[] = [];
  public minDate: Date;
  public focusedVariable: Data<EmpVariableAttributes>;
  public toggledVariable: Data<EmpVariableAttributes>;

  public variableOutsideContract = false;
  public isConfirmButtonVisible = false;

  public triggerSortByTranslationPipe: boolean = true;

  public isCreation = false;
  public threePeriodView = false;

  public isVariablesV2Enabled = false;

  @ViewChild('begindate')
  public beginDate: DatePickerInputComponent;

  @ViewChild('enddate')
  public endDate: DatePickerInputComponent;

  @ViewChild(VariableZonesComponent)
  public variableZones: VariableZonesComponent;
  public variableTypesTree: Array<TreeNode<any>>;
  private cliRegId$ = new BehaviorSubject<number>(null);
  public objectKeys = Object.keys;

  public variableRecurrence = Object.values(VariableRecurrence);
  public variableInstancesRecurrenceType: { [name: string]: string } = {};

  public payPeriodsList: Data<PayPeriodAttributes>[];

  constructor(private formBuilder: UntypedFormBuilder,
              private employeeVariablesService: EmployeeVariablesService,
              private modalService: ModalService,
              private ref: ChangeDetectorRef,
              private translateService: TranslateService,
              private toastrService: ToastrService) {
    super();
  }

  public ngOnInit() {
    this.employeeVariablesService.isEmployeeVariablesRecurring()
      .pipe(
        tap(recurrent => {
          this.isVariablesV2Enabled = recurrent;
        })
      ).subscribe();

    this.cancelEditItemModalCallback = this.onEditItemModalCancel.bind(this);
    this.submitEditItemModalCallback = this.onEditItemModalSubmit.bind(this);
    this.translateService.onLangChange.pipe(
      takeUntil(this.componentDestroyed$)
    ).subscribe(() => this.triggerSortByTranslationPipe = !this.triggerSortByTranslationPipe);

    this.cliRegId$.pipe(
      filter(Boolean),
      switchMap((cliRegId: number) => forkJoin({
        elementTypes: this.employeeVariablesService.listVariableElementTypes(cliRegId),
        variablesRecurrence: this.employeeVariablesService.getVisibleVariablesRecurrence(cliRegId)
      }).pipe(
        tap(({elementTypes, variablesRecurrence}) => {
          const {data} = elementTypes;
          this.typeArrayData = data || [];
          this.createInfoForm();
          this.onRecurrenceTypeChanged(this.infoForm.get('recurrenceType')?.value);
          this.variableInstancesRecurrenceType = variablesRecurrence;
        }),
        finalize(() => this.refreshView())
      )),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public onVariableTypeChanged(itemType: AnyData) {
    if (!itemType.attributes) {
      this.infoForm.get('name')?.setValue('', {emitEvent: true});
      return;
    }

    const newItem = Object.assign(
      {isNew: true, justAdded: true},
      this.createNewItemForEditedPayPeriod(
        itemType.attributes.name,
        itemType.attributes.unitTransId,
        itemType.attributes.transId)
    );

    this.editElement(newItem, false);
  }

  public onRecurrenceTypeChanged(recurrenceType: string) {
    let _typeArrayData = [];

    if (this.isVariablesV2Enabled) {
      if (recurrenceType) {
        _typeArrayData = this.typeArrayData.filter((variable: Data<EmpVariableAttributes>) => {
            return [recurrenceType, VariableRecurrence.BOTH].includes(variable.attributes.recurrenceType);
          }
        ) || [];
      } else {
        _typeArrayData = this.typeArrayData || [];
      }
    } else {
      _typeArrayData = this.typeArrayData.filter((variable: Data<EmpVariableAttributes>) => {
          return variable.attributes.recurrenceType === VariableRecurrence.NON_RECURRENT;
        }
      ) || [];
    }

    const types = groupBy(_typeArrayData, element => at(element, 'attributes.familyTransId'));
    this.variableTypesTree = Object.keys(types).map(type => ({
      text: type,
      value: type,
      data: type,
      selectable: false,
      visible: true,
      children: getChildren(types, type)
    }));

    this.infoForm.get('recurrenceType')?.setValue(recurrenceType, {emitEvent: false});
    this.infoForm.get('isBeginDateLinkedToContract')?.setValue(
      recurrenceType === VariableRecurrence.RECURRENT,
      {emitEvent: false}
    );
    this.infoForm.get('isEndDateLinkedToContract')?.setValue(
      recurrenceType === VariableRecurrence.RECURRENT,
      {emitEvent: false}
    );
    this.infoForm.get('name')?.setValue('', {emitEvent: false});
  }

  public ngOnChanges(changes: SimpleChanges): any {
    this.employeeVariablesService.getSelectedVariable.pipe(
      takeUntil(this.componentDestroyed$)
    ).subscribe(value => this.selectedVariable = value);

    if (changes.contract) {
      this.payPeriodId = undefined;
      this.minDate = strToDate(this.getContractBeginDate());
    }

    this.noPayPeriods = false;
    this.fetchVariableElementsAndTypesAndPayPeriods();
  }

  public onViewModeChange() {
    const payPeriodId = this.editedPayPeriod.id;
    this.payPeriodsList = [];
    this.fetchVariableElementsAndTypesAndPayPeriods(payPeriodId);
  }

  public isDirty(): boolean {
    return this._isDirty;
  }

  public isChanged(): boolean {
    const {creations, deletions, updates} = this.getChanges();
    return creations.length > 0 || deletions.length > 0 || updates.length > 0;
  }

  public isFormValid() {
    return !!this.infoForm.get('name')?.value && this.infoForm && this.infoForm.valid
      && this.isBeginBeforeEnd && this.getDatePickerValid()
      && this.variableZones && this.variableZones.isFormValid();
  }

  public navigateLeft() {
    this.canNavigate()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.payPeriodId = this.previousPayPeriod.id;
        this.fetchPayPeriodsAndVariableElements();
      });

  }

  public navigateRight() {
    this.canNavigate()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.payPeriodId = this.nextPayPeriod.id;
        this.fetchPayPeriodsAndVariableElements();
      });
  }

  public navigateTo(id: number): void {
    this.canNavigate()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.payPeriodId = id;
        this.fetchPayPeriodsAndVariableElements();
      });
  }

  public openModal() {
    const recurrenceType = this.isVariablesV2Enabled ? VariableRecurrence.RECURRENT : VariableRecurrence.NON_RECURRENT;
    this.infoForm.get('recurrenceType')?.setValue(recurrenceType, {emitEvent: false});
    this.onRecurrenceTypeChanged(recurrenceType);
    this.isCreation = true;
    this.modalService.openModalWithId('edit-line', true);
  }

  public save(skipLoad: boolean = false) {
    const saved: Subject<boolean> = new Subject<boolean>();
    const changes = this.getChanges();
    const hasChanges = changes.creations.length > 0 || changes.deletions.length > 0 || changes.updates.length > 0;

    if (hasChanges) {
      this.loading = true;
      this.employeeVariablesService.saveVariableElements(changes)
        .pipe(finalize(() => {
          saved.next(true);
          saved.complete();
          this.payPeriodsList = [];
        }))
        .subscribe(() => {
          if (!skipLoad) {
            this.fetchPayPeriodsAndVariableElements();
          }
        });
    } else {
      saved.next(true);
      saved.complete();
    }

    return saved;
  }

  public markAsDirty(isDirty: boolean = true) {
    this._isDirty = isDirty;
  }

  public markElementAsModified(element: Data<EmpVariableAttributes>): void {
    const variableType = this.variableElements[element.attributes.name];
    const variable = variableType.find((v: Data<EmpVariableAttributes>) => v.id === element.id)
    if (variable) {
      variable.modified = true;
      this._isDirty = true;
    }
  }

  public drawLine() {
    this.isLineDraw = !this.isLineDraw;
  }

  public fetchVariableElementsAndTypesAndPayPeriods(lastUsedPayperiod: number | undefined = undefined) {
    this.reset();

    if (!isPresent(this.cliRegId)) {
      return;
    }
    this.cliRegId$.next(this.cliRegId);

    if (this.contract && this.contract.id) {
      this.fetchPayPeriodsAndVariableElements(lastUsedPayperiod);
    }
  }

  public checkPeriodLimit() {
    if (moment(this.getContractBeginDate())
      .isSameOrAfter(this.editedPayPeriod.attributes.begindate, 'month')) {
      this.previousPayPeriod = null;
    }
  }

  public canNavigate(): Observable<boolean | Subject<boolean>> {
    if (this.isDirty() && this.isChanged()) {
      return from(
        this.modalService.dynamicConfirmModal({
          title: 'DIALOG.you_have_unsaved_changes',
          text: 'DIALOG.you_have_unsaved_changes?',
          confirmButton: 'FORMS.discard_all_changes',
          cancelButton: 'FORMS.keep_editing'
        }).then(choice => {
          return choice;
        })
      );
    }
    return of(true);
  }

  public fetchPayPeriodsAndVariableElements(lastUsedPayperiod: number | undefined = undefined) {
    this.loading = true;
    this.ref.markForCheck();
    this.variableElements = {};
    if (this.selectedVariable) {
      this.payPeriodId = this.selectedVariable.payPeriodId;
    }

    if (lastUsedPayperiod) {
      this.payPeriodId = lastUsedPayperiod;
    }

    return this.employeeVariablesService
      .getContractPayPeriodRange(this.contract.id, this.payPeriodId)
      .pipe(
        finalize(() => {
          if (!this.noPayPeriods) {
            this.checkPeriodLimit();
          }
          this.markAsDirty(false);
        }),
        catchError(() => of([]))
      )
      .subscribe(payPeriods => {
        if (!payPeriods) {
          this.noPayPeriods = true;
          return;
        }

        this.payPeriods = payPeriods.data || [];

        if (this.payPeriods.length < 2) {
          this.noPayPeriods = true;
          return;
        }
        if (this.payPeriods.length === 2) {
          if (!this.payPeriodId) {
            if (this.payPeriods[0].attributes.status === 'ONGOING') {
              this.payPeriods = [null, ...this.payPeriods];
            } else {
              this.payPeriods = [...this.payPeriods, null];
            }
          } else {
            if (this.selectedVariable) {
              if (this.payPeriods[0].id === this.selectedVariable.payPeriodId) {
                this.payPeriods = [null, ...this.payPeriods];
              } else {
                this.payPeriods = [...this.payPeriods, null];
              }
            } else {
              if (this.previousPayPeriod !== null && this.payPeriodId === this.previousPayPeriod.id) {
                this.payPeriods = [null, ...this.payPeriods];
              }
              if (this.nextPayPeriod !== null && this.payPeriodId === this.nextPayPeriod.id) {
                this.payPeriods = [...this.payPeriods, null];
              }
            }
          }
        }

        this.editedPayPeriod = this.payPeriods[1];
        this.previousPayPeriod = this.payPeriods[0];
        this.nextPayPeriod = this.payPeriods[2];

        const {left, right} = this.getDateRange();

        this.employeeVariablesService
          .listVariableElements(this.contract.id, left, right)
          .pipe(finalize(() => {
            this.deletedVariableElements = [];
            this.refreshView();
          }))
          .subscribe(result => {
            this.variableElements = this.createVariablesList(result.data);

            this.checkPeriodLimit();
            this.checkStartDateContract();
            this.getPeriods();

            if (this.selectedVariable && this.selectedVariable.name) {
              this.selectedItemType = this.typeArrayData
                .find(type => type.attributes.name === this.selectedVariable.name);
              if (!this.variableElements[this.selectedVariable.name]) {
                if (this.selectedItemType && !this.selectedVariable.isReadOnly) {
                  this.addItemToExistingVariable(this.selectedItemType);
                }
              } else if (this.variableElements[this.selectedVariable.name].length === 1) {
                if (!this.selectedVariable.isReadOnly) {
                  this.focusedVariable = this.variableElements[this.selectedVariable.name][0];
                }
              } else {
                this.toggledVariable = this.variableElements[this.selectedVariable.name][0];
              }
              this.employeeVariablesService.updateSelectedVariable(null);
            }
          });
      });
  }

  public getDateRange() {
    const left = !this.threePeriodView ? this.editedPayPeriod.attributes.begindate : (this.payPeriods[0] || this.payPeriods[1]).attributes.begindate;
    const right = !this.threePeriodView ? this.editedPayPeriod.attributes.enddate : (this.payPeriods[2] || this.payPeriods[1]).attributes.enddate;

    return {left, right};
  }

  public createVariablesList(variables: Data<EmpVariableAttributes>[]): {
    [name: string]: Data<EmpVariableAttributes>[]
  } {
    const variableElements = groupBy(variables, element => at(element, 'attributes.name'));
    // Add variable missing variable elements for edited period
    const editedRange = moment.range(
      moment(this.editedPayPeriod.attributes.begindate),
      moment(this.editedPayPeriod.attributes.enddate)
    );
    Object.keys(variableElements).forEach(name => {
      variableElements[name].forEach(item => item.attributes._value = item.attributes.value);

      const existingForEditedPayPeriod = variableElements[name].find(element => {
        const elementRange = moment.range(
          moment(element.attributes.begindate),
          moment(element.attributes.enddate)
        );
        return elementRange.overlaps(editedRange) || elementRange.start.isSame(editedRange.end) || elementRange.end.isSame(editedRange.start);
      });

      if (!existingForEditedPayPeriod) {
        const unitTransId = variableElements[name][0].attributes.unitTransId;
        const transId = variableElements[name][0].attributes.transId;
        const variableType = this.typeArrayData.find((x: Data<EmpVariableAttributes>) => x.attributes.name === variableElements[name][0].attributes.name);
        this.infoForm.get('recurrenceType')?.setValue(variableType.attributes.recurrenceType);
        variableElements[name].push(this.createNewItemForEditedPayPeriod(name, unitTransId, transId));
      }
    });
    return variableElements;
  }

  public createNewItemForEditedPayPeriod(name: string, unitTransId: string, transId?: string) {
    const isRecurrent = this.infoForm.get('recurrenceType')?.value === VariableRecurrence.RECURRENT;
    return {
      id: undefined,
      attributes: {
        name,
        transId,
        unitTransId,
        id: undefined,
        begindate: this.editedPayPeriod.attributes.begindate,
        isBeginDateLinkedToContract: isRecurrent,
        enddate: this.editedPayPeriod.attributes.enddate,
        isEndDateLinkedToContract: isRecurrent,
        value: undefined,
        empContract: this.contract.id,
        evtype: 'VARIABLE'
      }
    };
  }

  public checkStartDateContract() {
    this.isLongFuture = false;
    this.payPeriodId = undefined;

    const periodDuration = this.editedPayPeriod.attributes.serie.duration;
    if (this.nextPayPeriod
      && moment(this.getContractBeginDate()).isAfter(this.nextPayPeriod.attributes.begindate)) {
      this.employeeVariablesService.getPayPeriodByDateAndPaySerieDuration(this.getContractBeginDate(), periodDuration)
        .pipe(
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe((response) => {
          if (response?.id) {
            this.payPeriodId = response.id;
            this.isLongFuture = false;
            this.fetchVariableElementsAndTypesAndPayPeriods();
          } else {
            this.isLongFuture = true;
          }
          this.ref.markForCheck();
        });
    } else {
      this.loading = false;
    }
  }

  public editElement(element: Data<EmpVariableAttributes>, emit = true) {
    const selectedType = this.typeArrayData.find(type => type.attributes.name === element.attributes.name);
    this.onRecurrenceTypeChanged(selectedType.attributes.recurrenceType);
    this.infoForm.get('name')?.setValue(selectedType, {emitEvent: emit});

    this.editedElement = element;
    this.evAmount = this.editedElement.attributes.value;
    if (this.editedElement.attributes.enddate !== DATE_MAX) {
      this.editedElement.attributes.userenddate = moment(this.editedElement.attributes.enddate).format(DATE_FORMAT);
    }
    this.infoForm.get('isEndDateLinkedToContract')?.setValue(this.editedElement.attributes.isEndDateLinkedToContract);

    if (this.editedElement.attributes.begindate !== DATE_MIN) {
      this.editedElement.attributes.userbegindate = moment(this.editedElement.attributes.begindate).format(DATE_FORMAT);
    }
    this.infoForm.get('isBeginDateLinkedToContract')?.setValue(this.editedElement.attributes.isBeginDateLinkedToContract);

    this.checkVariableOutsideContractDates();
    this.ref.markForCheck();
  }

  public checkVariableOutsideContractDates() {
    if (this.editedElement.attributes.isEndDateLinkedToContract) {
      this.variableOutsideContract = false;
      return;
    }
    const varEndDate = new Date(this.editedElement.attributes.userenddate);
    const contractEndDate = new Date(this.getContractEndDate());

    if (varEndDate > contractEndDate) {
      this.variableOutsideContract = true;
    } else {
      this.variableOutsideContract = false;
    }
  }

  public onEndDateLinkedToContractChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    if (this.editedElement?.attributes) {
      this.editedElement.attributes.isEndDateLinkedToContract = isChecked;
    }

    this.infoForm.get('isEndDateLinkedToContract')?.setValue(isChecked);
    this.checkVariableOutsideContractDates();

    this.ref.markForCheck();
  }

  public onBeginDateLinkedToContractChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    if (this.editedElement?.attributes) {
      this.editedElement.attributes.isBeginDateLinkedToContract = isChecked;
    }

    this.infoForm.get('isBeginDateLinkedToContract')?.setValue(isChecked);
    this.checkVariableOutsideContractDates();

    this.ref.markForCheck();
  }

  public formVariableZoneLoaded() {
    this.modalService.openModalWithId('edit-line', true);
  }

  public onEditItemModalCancel(): Promise<void> {
    this.createInfoForm();
    this.editedElement = undefined;
    this.employeeVariablesService.updateSelectedVariable(null);
    this.isConfirmButtonVisible = false;
    this.variableOutsideContract = false;
    this.isCreation = false;
    this.modalService.modalClosed.emit('edit-line');
    return Promise.resolve();
  }

  public onEditItemModalSubmit(): Observable<ModalCommand> {
    const isNew = this.editedElement.isNew;
    const justAdded = this.editedElement.justAdded;
    this.editedElement.attributes.value = this.evAmount;

    if (!isNew) {
      this.editedElement.modified = true;
    }

    if (isNew && justAdded) {
      if (!this.variableElements[this.editedElement.attributes.name]) {
        this.variableElements[this.editedElement.attributes.name] = [];
      }
      this.editedElement.justAdded = false;
      this.variableElements[this.editedElement.attributes.name].push(this.editedElement);
    }

    if (this.editedElement.attributes.userenddate) {
      this.editedElement.attributes.enddate = moment(this.editedElement.attributes.userenddate).format(DATE_FORMAT);
    } else {
      this.editedElement.attributes.enddate = DATE_MAX;
    }

    this.editedElement.attributes.isBeginDateLinkedToContract = this.infoForm.get('isBeginDateLinkedToContract')?.value;

    if (this.editedElement.attributes.userbegindate) {
      this.editedElement.attributes.begindate = moment(this.editedElement.attributes.userbegindate).format(DATE_FORMAT);
    } else {
      this.editedElement.attributes.begindate = DATE_MIN;
    }

    this.editedElement.attributes.isEndDateLinkedToContract = this.infoForm.get('isEndDateLinkedToContract')?.value;

    // just for be sure that element was edited.
    this.editedElement.attributes._value = null;
    this.editedElement = undefined;
    this.updateVariableElements();
    this.employeeVariablesService.updateSelectedVariable(null);
    this.markAsDirty();
    this.createInfoForm();
    this.isConfirmButtonVisible = false;
    this.variableOutsideContract = false;
    this.refreshView();
    this.isCreation = false;
    this.modalService.modalClosed.emit('edit-line');

    return of({
      state: ModalState.VALID,
      message: 'Variable element updated',
      duration: 1500
    });
  }

  public getTitleModal(): string {
    if (this.isReadOnly) {
      return this.translateService.instant('PAYROLL_PREPARE.view_variable_element');
    }
    if (!this.editedElement || this.editedElement.justAdded || !this.editedElement.attributes?.value) {
      return this.translateService.instant('PAYROLL_PREPARE.new_variable_element');
    }
    return this.translateService.instant('PAYROLL_PREPARE.edit_variable_element');
  }

  public getChanges(): VariableElementChanges {
    if (this.variableElements !== undefined) {
      const editedVariables: Data<EmpVariableAttributes>[] = [];
      Object.keys(this.variableElements).forEach(key => {
        editedVariables.push(...this.variableElements[key]);
      });

      const creations = editedVariables.filter(v => !isPresent(v.id) && isPresent(v.attributes.value));
      const deletions = [
        ...editedVariables.filter(v => isPresent(v.id) && !v.attributes.value),
        ...this.deletedVariableElements.filter(v => isPresent(v.id))
      ];
      const updates = editedVariables.filter(
        v => isPresent(v.id) && v.attributes.value && v.attributes._value !== v.attributes.value
      );

      return {creations, deletions, updates};
    } else {
      return {creations: [], deletions: [], updates: []};
    }
  }

  public addItemToExistingVariable(itemType: Data<EmpVariableAttributes>) {
    if (!this.variableElements[itemType.attributes.name]) {
      this.variableElements[itemType.attributes.name] = [];
    }

    this.variableElements[itemType.attributes.name].push(
      Object.assign(
        {isNew: true, justAdded: true},
        this.createNewItemForEditedPayPeriod(
          itemType.attributes.name,
          itemType.attributes.unitTransId,
          itemType.attributes.transId)
      )
    );
    this.updateVariableElements();
    this.editedElement = this.variableElements[itemType.attributes.name].last();
    this.editElement(this.editedElement);
    this.employeeVariablesService.updateSelectedVariable(null);
    this.markAsDirty();
  }

  public confirmDeleteModal(callback: () => void) {
    return this.modalService.confirm({
      'title': 'DIALOG.confirm',
      'text': 'DIALOG.confirm_variable_deletion',
      'type': 'danger'
    }).then((confirmed: boolean) => {
      if (confirmed) {
        callback();
        this.modalService.modalClosed.emit('edit-line');
      }
    });
  }

  public elementDeleted(data: any) {
    const callback = () => {
      const element = this.variableElements[data.type].find(x => x.id === data.item.id);
      if (element) {
        const index = this.variableElements[data.type].indexOf(element);
        this.deletedVariableElements.push(element);
        this.variableElements[data.type].splice(index, 1);

        if (this.variableElements[data.type].length === 0) {
          delete this.variableElements[data.type];
        } else if (!data.isNested) {
          const emptyItem = this.createNewItemForEditedPayPeriod(
            element.attributes.name,
            element.attributes.unitTransId,
            element.attributes.transId);
          this.variableElements[data.type].splice(index, 0, emptyItem);
        }

        this.updateVariableElements();
        this.markAsDirty();
        this.refreshView();
      }
    };

    this.confirmDeleteModal(callback);
  }

  public elementDeletedNew(data: any) {
    const callback = () => {
      const index = this.variableElements[data.type].indexOf(data.item);
      this.variableElements[data.type].splice(index, 1);
      if (this.variableElements[data.type].length === 0) {
        delete this.variableElements[data.type];
      } else if (!data.isNested) {
        this.variableElements[data.type][index] = this.createNewItemForEditedPayPeriod(
          data.item.attributes.name,
          data.item.attributes.unitTransId,
          data.item.attributes.transId);
      }
      this.updateVariableElements();
      this.markAsDirty();
      this.refreshView();
    };

    this.confirmDeleteModal(callback);
  }

  public reset() {
    this.payPeriods = [];
    this.variableElements = {};
    this.previousPayPeriod = {};
    this.nextPayPeriod = {};
    this.refreshView();
  }

  public refreshView(): void {
    if (!this.isDestroyed) {
      this.ref.markForCheck();
    }
  }

  public updateVariableElements() {
    this.variableElements = cloneDeep(this.variableElements);
  }

  public onChangeDates() {
    this.checkDates();
    setTimeout(() => {
      this.checkDateBeforeStart();
      this.checkVariableOutsideContractDates();
      this.ref.markForCheck();
    });
  }

  public checkDateBeforeStart() {
    if (moment(this.editedElement.attributes.userbegindate).isBefore(this.getContractBeginDate())) {
      this.editedElement.attributes.userbegindate = this.getContractBeginDate();
    }
  }

  public checkDates() {
    this.isBeginBeforeEnd = moment(this.editedElement.attributes.userbegindate)
      .isSameOrBefore(this.editedElement.attributes.userenddate) || (!this.endDate || this.endDate.isEmpty);
    return this.isFormValid();
  }

  public getDatePickerValid() {
    let datepickerValid = true;
    if (this.beginDate && this.endDate) {
      datepickerValid = this.beginDate.isValid && (this.endDate.isValid || this.endDate.isEmpty);
    }
    return datepickerValid;
  }

  public checkValues() {
    const data = this.variableZones.getModifiedCardsData();
    if (data) {
      this.editedElement.attributes.values = data.values;
    }
  }

  public submitForm(modal) {
    this.checkDateBeforeStart();
    this.checkValues();
    if (this.checkDates()) {
      if (this.variableOutsideContract) {
        this.isConfirmButtonVisible = true;
      } else {
        this.confirmSubmission(modal);
      }
    }
  }

  public confirmSubmission(modal) {
    modal.submit();
    this.isConfirmButtonVisible = false;
  }

  public createInfoForm() {
    this.infoForm = this.formBuilder.group({
      recurrenceType: [
        this.isVariablesV2Enabled ? VariableRecurrence.RECURRENT : VariableRecurrence.NON_RECURRENT, ''
      ],
      name: ['', ''],
      beginDate: ['', GpValidators.validateISO8601Date],
      isBeginDateLinkedToContract: [{value: this.isVariablesV2Enabled, disabled: this.isReadOnly}, ''],
      endDate: ['', GpValidators.validateISO8601Date],
      isEndDateLinkedToContract: [{value: this.isVariablesV2Enabled, disabled: this.isReadOnly}, ''],
      prepValue: ['', ''],
      comment: ['', '']
    });
  }

  public getContractBeginDate() {
    return this.contract.attributes?.begindate ?? this.contract.begindate;
  }

  public getContractEndDate() {
    return this.contract.attributes?.enddate ?? this.contract.enddate;
  }

  private getPeriods(): void {
    this.employeeVariablesService.getPayPeriodsBySeriesId(
      this.editedPayPeriod.attributes.serie.id,
      this.getContractBeginDate(),
      this.getContractEndDate()
    )
      .pipe(
        tap(
          (payPeriods: any) => {
            this.payPeriodsList = payPeriods.data;
            this.ref.markForCheck();
          }
        )).subscribe();
  }

  public tableHeader(index: number): string {
    return this.employeeVariablesService.tableHeader(index);
  }
}
